import { useEffect, useMemo, useRef, useState } from "react";
import {
  AddOriginSearchOption,
  IFavoriteLocationExtension,
  IFavoriteOriginOptionExtension,
} from "../../../../types/IFavoriteOrigins";
import { sortByField } from "../../../../utils/Sort";
import NitroService from "../../../../utils/nitro.service";
import { getActiveOrigins, getRowKey } from "../FavoriteOriginHelper";
import { AddOriginOption } from "../components/OriginManagerHeader";

interface UseOriginManagerAddOriginParams {
  kochOrigins: IFavoriteLocationExtension[];
  competitorOrigins: IFavoriteLocationExtension[];
  selectedProduct: string;
  savedOrigins: IFavoriteLocationExtension[];
  newlyAddedOrigins: Record<string, IFavoriteLocationExtension[]>;
  deletedRowKeysByProduct: Record<string, Set<string>>;
}

export interface UseOriginManagerAddOriginResult {
  isAddOriginOpen: boolean;
  setIsAddOriginOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addOriginQuery: string;
  setAddOriginQuery: React.Dispatch<React.SetStateAction<string>>;
  optionsLoading: boolean;
  isAddingOriginLoading: boolean;
  setIsAddingOriginLoading: React.Dispatch<React.SetStateAction<boolean>>;
  favoriteOriginOptions: IFavoriteOriginOptionExtension[];
  setFavoriteOriginOptions: React.Dispatch<
    React.SetStateAction<IFavoriteOriginOptionExtension[]>
  >;
  filteredAddOriginOptions: AddOriginOption[];
  addOriginPopoverRef: React.RefObject<HTMLDivElement>;
  addOriginInputRef: React.RefObject<HTMLInputElement>;
}

export const useOriginManagerAddOrigin = ({
  kochOrigins,
  competitorOrigins,
  selectedProduct,
  savedOrigins,
  newlyAddedOrigins,
  deletedRowKeysByProduct,
}: UseOriginManagerAddOriginParams): UseOriginManagerAddOriginResult => {
  const [isAddOriginOpen, setIsAddOriginOpen] = useState(false);
  const [addOriginQuery, setAddOriginQuery] = useState("");
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [isAddingOriginLoading, setIsAddingOriginLoading] = useState(false);
  const [favoriteOriginOptions, setFavoriteOriginOptions] = useState<
    IFavoriteOriginOptionExtension[]
  >([]);
  const addOriginPopoverRef = useRef<HTMLDivElement | null>(null);
  const addOriginInputRef = useRef<HTMLInputElement | null>(null);

  // Fetch and set favorite origin options when the add origin popover is opened for the first time.
  useEffect(() => {
    if (!isAddOriginOpen || favoriteOriginOptions.length > 0) {
      return;
    }
    setOptionsLoading(true);
    const getLocations = async () => {
      try {
        const kochOptions = (await NitroService.getKochLocations()).map(
          (location) => ({
            ...location,
            products: location.products ?? [],
            competitor: false,
          }),
        );
        const competitorOptions = (
          await NitroService.getCompetitorLocations()
        ).map((location) => ({
          ...location,
          products: location.products ?? [],
          competitor: true,
        }));

        const locations = [...kochOptions, ...competitorOptions];
        const sorted: IFavoriteOriginOptionExtension[] = sortByField(
          locations,
          "name",
          "ASC",
        );
        setFavoriteOriginOptions(sorted);
      } catch {
        // handle error if needed
      } finally {
        setOptionsLoading(false);
      }
    };
    getLocations();
  }, [
    isAddOriginOpen,
    favoriteOriginOptions.length,
    kochOrigins,
    competitorOrigins,
  ]);

  const existingOriginSideKeys = useMemo(() => {
    const deletedKeys =
      deletedRowKeysByProduct[selectedProduct] ?? new Set<string>();
    const currentOrigins = getActiveOrigins(
      savedOrigins,
      newlyAddedOrigins[selectedProduct] ?? [],
      deletedKeys,
    );
    const keys = new Set<string>();
    currentOrigins.forEach((origin) => {
      keys.add(getRowKey(origin));
    });
    return keys;
  }, [
    savedOrigins,
    deletedRowKeysByProduct,
    newlyAddedOrigins,
    selectedProduct,
  ]);

  const filteredAddOriginOptions = useMemo(() => {
    const query = addOriginQuery.trim().toLowerCase();

    const mergedById = new Map<string, AddOriginSearchOption>();
    favoriteOriginOptions.forEach((option) => {
      const existing = mergedById.get(option.id) ?? {
        id: option.id,
        name: option.name,
        canAddKoch: false,
        canAddCompetitor: false,
      };

      if (option.competitor) {
        existing.competitorOption = option;
      } else {
        existing.kochOption = option;
      }

      mergedById.set(option.id, existing);
    });

    return Array.from(mergedById.values())
      .map((option) => {
        const hasExistingRowForLocation =
          existingOriginSideKeys.has(`koch-${option.id}`) ||
          existingOriginSideKeys.has(`competitor-${option.id}`);

        // indicates that this option can be added to the users koch or competitor lists of favorite origins
        // option must 1. have a koch or competitor option and must not already be added
        const canAddKoch =
          !!option.kochOption &&
          !existingOriginSideKeys.has(`koch-${option.id}`);
        const canAddCompetitor =
          !!option.competitorOption &&
          !existingOriginSideKeys.has(`competitor-${option.id}`);
        return {
          ...option,
          hasExistingRowForLocation,
          canAddKoch,
          canAddCompetitor,
        };
      })
      .filter((option) => {
        if (
          !option.canAddKoch &&
          !option.canAddCompetitor &&
          !option.hasExistingRowForLocation
        ) {
          return false;
        }
        if (!query) {
          return true;
        }
        return option.name.toLowerCase().includes(query);
      });
  }, [favoriteOriginOptions, addOriginQuery, existingOriginSideKeys]);

  useEffect(() => {
    if (!isAddOriginOpen) {
      return undefined;
    }

    const closeAddOriginPopover = () => {
      setIsAddOriginOpen(false);
    };

    const handlePointerDown = (event: MouseEvent) => {
      if (
        addOriginPopoverRef.current &&
        !addOriginPopoverRef.current.contains(event.target as Node)
      ) {
        closeAddOriginPopover();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeAddOriginPopover();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isAddOriginOpen, selectedProduct]);

  useEffect(() => {
    if (isAddOriginOpen) {
      addOriginInputRef.current?.focus();
    }
  }, [isAddOriginOpen]);

  return {
    isAddOriginOpen,
    setIsAddOriginOpen,
    addOriginQuery,
    setAddOriginQuery,
    optionsLoading,
    isAddingOriginLoading,
    setIsAddingOriginLoading,
    favoriteOriginOptions,
    setFavoriteOriginOptions,
    filteredAddOriginOptions,
    addOriginPopoverRef,
    addOriginInputRef,
  };
};
